import {Injectable} from '@angular/core';
import {HttpClient, HttpContext, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {RequestParam} from '../model/request-param';
import {RequestHeader} from '../model/request-header';
import {RequestHeaderUtil} from '../util/request-header.util';
import {CustomHttpUrlEncodingCodecUtil} from '../util/custom-http-url-encoding-codec.util';
import {environment} from '../../../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class HttpClientWrapperService {

    private readonly apiBaseUrl: string;

    private readonly requestOptions = {
        params: new HttpParams(),
        headers: new HttpHeaders()
    };

    constructor(private readonly http: HttpClient) {
        this.apiBaseUrl = environment.baseApiUrl;
    }

    get<T>(url: string, requestParams?: RequestParam[], requestHeaders?: RequestHeader[], errorCallback?: (error) => Observable<any>): Observable<T> {
        this.setRequestOptions(requestParams, url);
        return this.http.get<T>(this.apiBaseUrl + url, this.requestOptions);
    }

    getWithHttpContext<T>(url: string, requestParams: RequestParam[], httpContext: HttpContext): Observable<T> {
        this.setRequestOptions(requestParams, url);
        return this.http.get<T>(this.apiBaseUrl + url, {...this.requestOptions, context: httpContext});
    }

    post<T>(url: string, data?: any, requestParams?: RequestParam[], requestHeaders?: RequestHeader[], errorCallback?: (error) => Observable<any>): Observable<T> {
        this.setRequestOptions(requestParams, url);
        return this.http.post<T>(this.apiBaseUrl + url, data, this.requestOptions);
    }

    postWithHttpContext<T>(url: string, data: any, httpContext: HttpContext): Observable<T> {
        this.setRequestOptions([], url);
        return this.http.post<T>(this.apiBaseUrl + url, data, {...this.requestOptions, context: httpContext});
    }

    postForBlob(absoluteUrl: string, data?: any): Observable<Blob> {
        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        return this.http.post(absoluteUrl, data, {headers, responseType: 'blob'});
    }

    getForBlob(url: string, requestParams?: RequestParam[]): Observable<Blob> {
        this.setRequestOptions(requestParams, url);
        return this.http.get(this.apiBaseUrl + url, {...this.requestOptions, responseType: 'blob'});
    }

    getForBlobWithHttpContext(url: string, httpContext: HttpContext): Observable<Blob> {
        this.setRequestOptions([], url);
        return this.http.get(this.apiBaseUrl + url, {...this.requestOptions, responseType: 'blob', context: httpContext});
    }

    put<T>(url: string, data?: any, requestParams?: RequestParam[], requestHeaders?: RequestHeader[], errorCallback?: (error) => Observable<any>): Observable<T> {
        this.setRequestOptions(requestParams, url);
        return this.http.put<T>(this.apiBaseUrl + url, data, this.requestOptions);
    }

    patch<T>(url: string, data?: any, requestParams?: RequestParam[], requestHeaders?: RequestHeader[], errorCallback?: (error) => Observable<any>): Observable<T> {
        this.setRequestOptions(requestParams, url);
        return this.http.patch<T>(this.apiBaseUrl + url, data, this.requestOptions);
    }

    delete(url: string, requestParams?: RequestParam[], requestHeaders?: RequestHeader[], errorCallback?: (error) => Observable<any>) {
        this.setRequestOptions(requestParams, url);
        return this.http.delete(this.apiBaseUrl + url, this.requestOptions);
    }

    deleteWithRequestBody<T>(url: string, data?: any, requestParams?: RequestParam[], requestHeaders?: RequestHeader[], errorCallback?: (error) => Observable<any>): Observable<T> {
        const requestOptions = this.setRequestOptionsForDeleteWithRequestBody(requestParams, url, data);
        return this.http.delete<T>(this.apiBaseUrl + url, requestOptions);
    }

    private appendRequestParams(requestParams?: RequestParam[]): void {
        this.requestOptions.params = new HttpParams({encoder: new CustomHttpUrlEncodingCodecUtil()});
        if (requestParams !== null && requestParams !== undefined) {
            requestParams.forEach(requestParam =>
                this.requestOptions.params = this.requestOptions.params.append(requestParam.key, requestParam.value));
        }
    }

    private setRequestOptionsForDeleteWithRequestBody(requestParams: RequestParam[], url: string, data: any) {
        this.appendRequestParams(requestParams);
        this.requestOptions.headers = RequestHeaderUtil.addJwtToken(url);
        return {
            params: this.requestOptions.params,
            headers: this.requestOptions.headers,
            body: data,
            observe: 'body' as const
        };
    }

    private setRequestOptions(requestParams: RequestParam[], url: string): void {
        this.appendRequestParams(requestParams);
        this.requestOptions.headers = RequestHeaderUtil.addJwtToken(url);
    }
}
